import {
  CardConfig,
  CardKind,
  Placement,
} from "../DashboardHeader/DashboardParams";

/**
 * Explicit-placement grid layout engine for the metrics dashboard.
 *
 * The dashboard is divided into titled SECTIONS, each an independent "master
 * canvas" {@link MASTER_COLS} columns wide — the one true coordinate space
 * every stored {@link Placement} lives in. Sections lay out left-to-right,
 * one per tablet-landscape-class section footprint
 * ({@link SECTION_MULTI_MIN_WIDTH} + {@link SECTION_GAP}) of dashboard width,
 * and stack full-width below that.
 *
 * Within a section, the layout is FIRST resolved on the master canvas: each
 * card has an effective `{row, col, w, h}` — its stored placement, or a
 * flow-baseline position for un-placed cards — and claims its exact master
 * cell whenever it's free (so a card lands where it was dropped, gaps and
 * all); an occupied or overflowing cell bumps the card forward (column, then
 * row). The column-tier ladder is [{@link MASTER_COLS}, 2]: the 6-column
 * master renders fluidly from a 720px window up (see {@link MIN_TRACK}), and
 * anything narrower COMPRESSES straight to the 2-column device tier — cards
 * keep master reading order and dense-flow from the origin, with widths
 * clamped to the rendered columns (charts stretch to the full row). Stored
 * placements are untouched — widening the screen restores the master layout
 * exactly.
 *
 * Stored placements wider than the master, or with out-of-range anchors, are
 * handled defensively rather than rewritten: {@link packPlaced} clamps an
 * over-wide card to the master width, and treats a card whose anchor falls
 * outside the columns as not-validly-placed — flowing it in STRICTLY BELOW the
 * validly placed cards (never backfilling gaps beside them) rather than
 * clamping/bumping it in place.
 */

/** Master-canvas column count: every section is this many columns wide. */
export const MASTER_COLS = 6;

/**
 * The MINIMUM cell width (px) the cards stay legible at: a canvas shows N
 * columns only when N such cells (+ gutters) fit. Anchored at a 720px window
 * (the narrowest window that renders the full master): with the sidebar
 * hidden the canvas there is 720 − 40 (PageLayout padding) − 18
 * (SECTION_CHROME) = 662px, and MIN_TRACK = (662 − 5 × GRID_GAP) / 6 = 107
 * makes 6 × 107 + 5 × 4 = 662 fit it exactly. The multi-section thresholds
 * clear this anchor: {@link sectionsPerRowFor} goes N-up only when every
 * section still gets a full {@link SECTION_MULTI_MIN_WIDTH} (≥ the 662
 * minimum), so a section at any threshold renders its full master with room
 * to spare.
 */
export const MIN_TRACK = 107;

/** Gutter between grid cells (px); must match --grid-gap in the CSS. */
export const GRID_GAP = 4;

/** Gutter between sections on BOTH axes (px); must match the sections grid's
 *  gap in DashboardSections.module.css. Deliberately equal to PageLayout's
 *  `.page-content` padding (a hardcoded 20px, not a `--spacing-*` token) so
 *  the space between sections reads the same as the space between the app
 *  sidebar and the first section. This constant is px-by-contract — the
 *  layout engine measures CSS px — so if PageLayout's padding ever moves to
 *  a token, keep this literal in sync with that token's px value. */
export const SECTION_GAP = 20;

/** Section card chrome (px): padding ×2 + border ×2, which the canvas inside
 *  doesn't get to use. Must match DashboardSections.module.css. */
export const SECTION_CHROME = 18;

/**
 * One section's minimum on-screen footprint (px): the full 6-column master
 * at MIN_TRACK tracks, plus the section's own chrome — minWidthFor(6) +
 * SECTION_CHROME = 662 + 18 = 680. This is exactly the section width the
 * 720px window anchor buys (see {@link MIN_TRACK}): it's the footprint at
 * which a LONE full-width section still renders its full 6-column master, so
 * it governs the single-section 6-column → 2-column phone transition (via
 * {@link colsFor} on the section's content width). It is deliberately NOT the
 * unit {@link sectionsPerRowFor} divides by — that split uses the larger
 * {@link SECTION_MULTI_MIN_WIDTH}.
 */
export const SECTION_MIN_WIDTH = minWidthFor(MASTER_COLS) + SECTION_CHROME;

/**
 * The per-section footprint (px) the MULTI-section split
 * ({@link sectionsPerRowFor}) divides by — intentionally larger than the
 * 6-column minimum footprint {@link SECTION_MIN_WIDTH} (680). Sections don't
 * break into side-by-side columns until each would get at least a
 * tablet-landscape-class width (768), so a narrow-but-not-phone window keeps
 * one comfortably wide section rather than cramming two minimum-width ones
 * side by side. This is decoupled from {@link SECTION_MIN_WIDTH} on purpose:
 * a lone full-width section still renders all 6 columns down to 662 of
 * content (the phone transition is unchanged), but it takes a wider dashboard
 * before a SECOND section joins it on the row.
 */
export const SECTION_MULTI_MIN_WIDTH = 768;

/** A card's preferred footprint in grid cells. `minCols`×`minRows` is the
 *  smallest footprint the card stays legible at, and `defaultCols`×
 *  `defaultRows` is the footprint a freshly added (or never-placed) card
 *  takes. Neither bounds RESIZING upward — stored widths are kept as-is,
 *  clamped only by the canvas capacity (see `packPlaced` /
 *  `usePlacedResize`). */
export interface CardSize {
  minCols: number;
  minRows: number;
  defaultCols: number;
  defaultRows: number;
}

/** The footprint each card kind takes on the master canvas: a trend KPI is a
 *  1×1 cell; a chart defaults to a 3×3 block (half the 6-column master's
 *  width) but can shrink down to a 2×2 minimum (resizable afterwards in
 *  whole cells). */
export function preferredSize(kind: CardKind): CardSize {
  return kind === "chart"
    ? { minCols: 2, minRows: 2, defaultCols: 3, defaultRows: 3 }
    : { minCols: 1, minRows: 1, defaultCols: 1, defaultRows: 1 };
}

/** A card's absolute placement in the rendered grid (1-based grid lines). */
export interface PlacedCard {
  colStart: number;
  cols: number;
  rowStart: number;
  rows: number;
}

/** An empty 1×1 rendered cell — a drop target during drags / an add slot. */
export interface EmptyCell {
  key: string;
  colStart: number;
  rowStart: number;
}

/** The resolved layout of one section: where each card renders, the empty
 *  cells around them, and — when `shrunk` — the master-canvas layout that
 *  `placements` is a compressed projection of. */
export interface GridLayout {
  placements: Map<string, PlacedCard>;
  empties: EmptyCell[];
  columns: number;
  shrunk: boolean;
  /** The layout resolved on the master canvas (the stored coordinate space).
   *  Identical to `placements` when not shrunk. */
  master: Map<string, PlacedCard>;
  /** Highest row occupied by any placed card. Rows beyond this are trailing
   *  add-card affordance rows, not content canvas — used to split
   *  grid-template-rows (explicit, full height) from grid-auto-rows
   *  (implicit, compact). Zero when the section has no cards. */
  contentRows: number;
}

/** Content width (px) needed to show `n` columns at the minimum cell size. */
export function minWidthFor(n: number): number {
  return n * MIN_TRACK + (n - 1) * GRID_GAP;
}

/**
 * Largest column tier that fits `width`: the full master count when its
 * minimum width fits, else the 2-column device tier — the ladder is
 * [masterCols, 2]. (There are no intermediate tiers any more: the 6-column
 * master's cells are sized to fit fluidly from the 720px window anchor up,
 * so the only compression worth keeping is the phone tier. Odd master
 * counts and their parity-matched tiers went with the old 8-column world.)
 */
export function colsFor(width: number, masterCols: number): number {
  if (width >= minWidthFor(masterCols)) {
    return masterCols;
  }
  return Math.min(2, masterCols);
}

/**
 * How many sections share a row at `totalWidth`: as many tablet-landscape-
 * class section footprints as fit — N sections need
 * N × {@link SECTION_MULTI_MIN_WIDTH} + (N − 1) × {@link SECTION_GAP}, so
 * sections go 2-up at 2 × 768 + 20 = 1556px of dashboard width, 3-up at
 * 2344px, 4-up at 3132px, and so on. (Adding SECTION_GAP to both sides of the
 * division turns the N-per-row inequality into one floor.)
 *
 * When `sectionCount` is given, the width-derived count is additionally
 * capped at `sectionCount + 1`: the Add-section ghost occupies a real grid
 * track, so N sections can fill at most N + 1 columns — any further
 * width-derived columns could never be filled and would only shrink the
 * sections that do exist. (A dashboard with one section on an ultrawide
 * container renders that section + the add slot at half width each, not at a
 * quarter width with two dead tracks.)
 */
export function sectionsPerRowFor(
  totalWidth: number,
  sectionCount: number,
): number {
  const byWidth = Math.max(
    1,
    Math.floor(
      (totalWidth + SECTION_GAP) / (SECTION_MULTI_MIN_WIDTH + SECTION_GAP),
    ),
  );
  return Math.max(1, Math.min(byWidth, sectionCount + 1));
}

/** The usable canvas width inside one section card (content box). */
export function sectionWidthFor(
  totalWidth: number,
  sectionsPerRow: number,
): number {
  return (
    (totalWidth - (sectionsPerRow - 1) * SECTION_GAP) / sectionsPerRow -
    SECTION_CHROME
  );
}

/**
 * Default column span for an un-placed (or newly added) card at the given
 * capacity: the kind's default width, clamped to what fits. On the narrowest
 * canvases (capacity ≤ 3) that clamp already stretches a chart across the
 * full row — a partial-width chart would leave a dead column and cramp its
 * axis labels.
 */
export function defaultSpanFor(kind: CardKind, capacity: number): number {
  const { defaultCols } = preferredSize(kind);
  if (!Number.isFinite(capacity)) {
    return defaultCols;
  }
  return Math.min(defaultCols, capacity);
}

interface EffectiveRect {
  id: string;
  row: number;
  col: number;
  w: number;
  h: number;
}

/**
 * Flow positions for the FLOWED cards — the ones that don't claim an explicit
 * cell (un-placed cards, plus cards whose stored anchor is out of bounds; see
 * {@link packPlaced}). Walks them in list order at their effective footprint
 * (`size`), left-to-right wrapping at `capacity`. Callers pass only the flowed
 * cards, so every card given here contributes a baseline.
 */
function flowBaseline(
  cards: readonly CardConfig[],
  capacity: number,
  size: (card: CardConfig) => { w: number; h: number },
): Map<string, EffectiveRect> {
  const map = new Map<string, EffectiveRect>();
  let col = 1;
  let row = 1;
  let rowMaxH = 0;
  for (const card of cards) {
    const { w, h } = size(card);
    if (col + w - 1 > capacity) {
      row += rowMaxH || 1;
      col = 1;
      rowMaxH = 0;
    }
    map.set(card.id, { id: card.id, row, col, w, h });
    col += w;
    rowMaxH = Math.max(rowMaxH, h);
  }
  return map;
}

/**
 * Place items one after another onto a `cap`-wide occupancy grid. Each item
 * starts scanning at its own (row, col) — or at the origin when `fromOrigin`,
 * which yields the dense "every row fills before the next" projection — and
 * lands on the first free spot (columns, then rows).
 */
function packCells(
  items: readonly EffectiveRect[],
  cap: number,
  fromOrigin: boolean,
): Map<string, PlacedCard> {
  const out = new Map<string, PlacedCard>();
  const occupied = new Set<string>();
  const fits = (r: number, c: number, w: number, h: number) => {
    if (c + w - 1 > cap) {
      return false;
    }
    for (let rr = r; rr < r + h; rr++) {
      for (let cc = c; cc < c + w; cc++) {
        if (occupied.has(`${rr}-${cc}`)) {
          return false;
        }
      }
    }
    return true;
  };
  for (const it of items) {
    let r = fromOrigin ? 1 : it.row;
    let c = fromOrigin ? 1 : it.col;
    while (!fits(r, c, it.w, it.h)) {
      c++;
      if (c + it.w - 1 > cap) {
        c = 1;
        r++;
      }
    }
    out.set(it.id, { colStart: c, cols: it.w, rowStart: r, rows: it.h });
    for (let rr = r; rr < r + it.h; rr++) {
      for (let cc = c; cc < c + it.w; cc++) {
        occupied.add(`${rr}-${cc}`);
      }
    }
  }
  return out;
}

/** Last grid row any placed card reaches; 0 when there are no cards. */
function maxOccupiedRow(placements: ReadonlyMap<string, PlacedCard>): number {
  let maxRow = 0;
  for (const p of placements.values()) {
    maxRow = Math.max(maxRow, p.rowStart + p.rows - 1);
  }
  return maxRow;
}

/** Every cell covered by a placed card, as `"row-col"` keys. */
function occupiedCells(
  placements: ReadonlyMap<string, PlacedCard>,
): Set<string> {
  const occupied = new Set<string>();
  for (const p of placements.values()) {
    for (let r = p.rowStart; r < p.rowStart + p.rows; r++) {
      for (let c = p.colStart; c < p.colStart + p.cols; c++) {
        occupied.add(`${r}-${c}`);
      }
    }
  }
  return occupied;
}

/** Append an {@link EmptyCell} for every unoccupied cell in rows
 *  `fromRow`..`toRow` (inclusive) across `columns`. */
function fillEmptyRows(
  empties: EmptyCell[],
  occupied: ReadonlySet<string>,
  fromRow: number,
  toRow: number,
  columns: number,
): void {
  for (let r = fromRow; r <= toRow; r++) {
    for (let c = 1; c <= columns; c++) {
      if (occupied.has(`${r}-${c}`)) {
        continue;
      }
      empties.push({ key: `e-${r}-${c}`, colStart: c, rowStart: r });
    }
  }
}

/**
 * Resolve one section's layout (see the module doc for the algorithm). Cards
 * are the section's cards in list order; `capacity` is the rendered column
 * count (≤ `masterCols`).
 */
export function packPlaced(
  cards: readonly CardConfig[],
  capacity: number,
  masterCols: number,
): GridLayout {
  // A stored placement is OUT OF BOUNDS when its anchor doesn't fit the master
  // columns — the same column-fit test the placement guard uses (see
  // {@link fitsAt}): `col < 1`, or the footprint extends past the last column.
  // Rows are unbounded (the canvas grows downward), so this is a COLUMN
  // condition only. A trend renders 1×1, so only its anchor column matters; a
  // chart is tested at its STORED width, so one wider than the master (which
  // can't fit at any anchor) is always out of bounds.
  const outOfBounds = (card: CardConfig): boolean => {
    const p = card.placement;
    if (!p) {
      return false;
    }
    const w = card.kind === "trend" ? 1 : p.w;
    return p.col < 1 || p.col + w - 1 > masterCols;
  };
  // Effective rendered footprint. Trend KPI tiles are locked to 1×1: a larger
  // stored placement (from a stale URL or saved dashboard) still renders 1×1.
  // A placed chart keeps its stored size, with width clamped to the master —
  // nothing renders wider than the canvas. An un-placed chart takes its
  // default span. Charts are otherwise free-resizable beyond their
  // preferredSize defaults, up to the canvas capacity.
  const footprintOf = (card: CardConfig): { w: number; h: number } => {
    if (card.kind === "trend") {
      return { w: 1, h: 1 };
    }
    const p = card.placement;
    if (p) {
      return { w: Math.min(p.w, masterCols), h: p.h };
    }
    return {
      w: defaultSpanFor(card.kind, masterCols),
      h: preferredSize(card.kind).defaultRows,
    };
  };

  // Cards that DON'T claim an explicit cell — un-placed cards AND cards whose
  // stored anchor is out of bounds — flow in below the validly placed cards.
  // Their baselines walk in list order at their effective footprint (see
  // {@link flowBaseline}); the block below is shifted down under the valid
  // content.
  const flows = cards.filter((card) => !card.placement || outOfBounds(card));
  const base = flowBaseline(flows, masterCols, footprintOf);

  const validExplicit: EffectiveRect[] = [];
  const flowed: EffectiveRect[] = [];
  for (const card of cards) {
    const { w, h } = footprintOf(card);
    const p = card.placement;
    if (p && !outOfBounds(card)) {
      validExplicit.push({
        id: card.id,
        row: Math.max(1, p.row),
        col: p.col,
        w,
        h,
      });
    } else {
      const b = base.get(card.id) ?? { id: card.id, row: 1, col: 1, w, h };
      flowed.push({ id: card.id, row: b.row, col: b.col, w: b.w, h: b.h });
    }
  }
  // Validly placed cards claim their exact cells first (sorted by row, then
  // col); a card that would collide with one already placed bumps forward
  // (column, then row) among the valid set only, so a placed card is never
  // displaced.
  validExplicit.sort((a, b) => a.row - b.row || a.col - b.col);
  const master = packCells(validExplicit, masterCols, false);

  // Flowed cards go STRICTLY BELOW the valid block: one row under the
  // lowest-occupied row of ALL valid content (row 1 when there is none), never
  // backfilling gaps beside or under the valid cards. Among themselves they
  // keep their reading-order flow baseline (left→right, wrapping at the
  // columns) — those baselines never overlap, so shifting the whole block down
  // by the valid height drops each card into fresh space with no gap-finding.
  let flowStart = 1;
  for (const p of master.values()) {
    flowStart = Math.max(flowStart, p.rowStart + p.rows);
  }
  for (const it of flowed) {
    master.set(it.id, {
      colStart: it.col,
      cols: it.w,
      rowStart: it.row + flowStart - 1,
      rows: it.h,
    });
  }

  // Compress below the master width (see the module doc): dense master-
  // reading-order flow, with widths clamped to the tier and charts stretched
  // to the full row on the narrowest canvases.
  const shrunk = capacity < masterCols;
  let rendered = master;
  if (shrunk) {
    const kindOf = new Map(cards.map((c) => [c.id, c.kind]));
    const ordered = [...master.entries()]
      .sort(
        (a, b) =>
          a[1].rowStart - b[1].rowStart || a[1].colStart - b[1].colStart,
      )
      .map(([id, m]) => {
        const w =
          kindOf.get(id) === "chart" && capacity <= 3
            ? capacity // narrowest view: charts always take the full row
            : Math.min(m.cols, capacity);
        return { id, row: 1, col: 1, w, h: m.rows };
      });
    rendered = packCells(ordered, capacity, true);
  }

  // Empty cells: every unoccupied rendered cell within the used range + a
  // trailing all-empty row, so there's always somewhere to drop or add.
  const maxRow = maxOccupiedRow(rendered);
  const empties: EmptyCell[] = [];
  fillEmptyRows(empties, occupiedCells(rendered), 1, maxRow + 1, capacity);
  return {
    placements: rendered,
    empties,
    columns: capacity,
    shrunk,
    master,
    contentRows: maxRow,
  };
}

/** A rendered-grid cell coordinate (1-based). */
export interface Cell {
  row: number;
  col: number;
}

/**
 * Translate a rendered cell into its MASTER-canvas cell (placements always
 * store master coordinates). On the full-width canvas they're the same. On a
 * compressed one, a cell inside a card maps to that card's master cell; an
 * empty cell maps to the slot right after its reading-order predecessor (or
 * the origin when there is none) — the closest master meaning a
 * dense-compressed gap has.
 */
export function masterCellFor(
  layout: GridLayout,
  cell: Cell,
  masterCols: number,
): Cell {
  if (!layout.shrunk) {
    return cell;
  }
  let pred: PlacedCard | null = null; // rendered cell of the predecessor
  let predMaster: PlacedCard | null = null;
  for (const [id, p] of layout.placements) {
    const m = layout.master.get(id);
    if (!m) {
      continue;
    }
    const inside =
      cell.row >= p.rowStart &&
      cell.row < p.rowStart + p.rows &&
      cell.col >= p.colStart &&
      cell.col < p.colStart + p.cols;
    if (inside) {
      return { row: m.rowStart, col: m.colStart };
    }
    const before =
      p.rowStart < cell.row ||
      (p.rowStart === cell.row && p.colStart < cell.col);
    const laterThanPred =
      !pred ||
      p.rowStart > pred.rowStart ||
      (p.rowStart === pred.rowStart && p.colStart > pred.colStart);
    if (before && laterThanPred) {
      pred = p;
      predMaster = m;
    }
  }
  if (!predMaster) {
    return { row: 1, col: 1 };
  }
  let row = predMaster.rowStart;
  let col = predMaster.colStart + predMaster.cols;
  if (col > masterCols) {
    row = predMaster.rowStart + predMaster.rows;
    col = 1;
  }
  return { row, col };
}

/** One placement save applied to the URL-backed card list. */
export interface PlacementSave {
  id: string;
  placement: Placement;
}

/**
 * True when a `w`×`h` footprint anchored at `cell` stays inside the master
 * columns and overlaps no card in `masterMap`. Ids in `ignore` are excluded
 * from the overlap check (the card being moved/resized, or every member of a
 * group drag). Rows are unbounded — the canvas grows downward freely.
 *
 * This is the placement guard: existing cards always keep their cells (the
 * least-recently-placed card wins), so any edit that would overlap one is
 * simply prevented rather than displacing it.
 */
export function fitsAt(
  masterMap: ReadonlyMap<string, PlacedCard>,
  cell: Cell,
  w: number,
  h: number,
  masterCols: number,
  ignore?: ReadonlySet<string>,
): boolean {
  if (cell.row < 1 || cell.col < 1 || cell.col + w - 1 > masterCols) {
    return false;
  }
  for (const [id, p] of masterMap) {
    if (ignore?.has(id)) {
      continue;
    }
    const overlaps =
      cell.row < p.rowStart + p.rows &&
      cell.row + h > p.rowStart &&
      cell.col < p.colStart + p.cols &&
      cell.col + w > p.colStart;
    if (overlaps) {
      return false;
    }
  }
  return true;
}

/**
 * Equalize empty-cell row counts across sections that share a visual row.
 *
 * When multiple sections sit side by side, CSS `align-items: stretch` makes
 * every section container as tall as the tallest in its row. This function
 * extends each shorter section's {@link GridLayout.empties} so the inner card
 * grid renders add-card slots all the way down to fill that height, instead
 * of leaving blank space below the last track.
 *
 * Mutates the `empties` arrays in place — they're freshly created by
 * {@link packPlaced} each render, so mutation is safe.
 */
export function equalizeEmptyRows(
  layouts: ReadonlyMap<string, GridLayout>,
  sectionIds: readonly string[],
  sectionsPerRow: number,
): void {
  if (sectionsPerRow <= 1) {
    return;
  }
  for (let start = 0; start < sectionIds.length; start += sectionsPerRow) {
    const rowIds = sectionIds.slice(start, start + sectionsPerRow);
    if (rowIds.length <= 1) {
      continue;
    }

    // Find the max row across sections in this visual row. A section's
    // maxRow is the highest row any placed card occupies; packPlaced already
    // added empties through maxRow + 1.
    let groupMax = 0;
    for (const id of rowIds) {
      const layout = layouts.get(id);
      if (!layout) {
        continue;
      }
      groupMax = Math.max(groupMax, maxOccupiedRow(layout.placements));
    }
    // packPlaced adds a trailing row (+1); match that for the tallest.
    const targetRow = groupMax + 1;

    for (const id of rowIds) {
      const layout = layouts.get(id);
      if (!layout) {
        continue;
      }
      const currentTrailing = maxOccupiedRow(layout.placements) + 1;
      if (currentTrailing >= targetRow) {
        continue;
      }
      fillEmptyRows(
        layout.empties,
        occupiedCells(layout.placements),
        currentTrailing + 1,
        targetRow,
        layout.columns,
      );
      // The equalized rows should render at full content-row height (matching
      // the taller section), so bump contentRows to the group max — the
      // compact trailing row sits beyond this.
      (layout as { contentRows: number }).contentRows = groupMax;
    }
  }
}

/**
 * How many of a section's grid rows render at the full content-row height
 * (`--grid-row-h`). Rows past this fall to the compact `grid-auto-rows` track
 * that keeps a trailing add-card row from eating a whole row of space.
 *
 * A section with no cards has no content rows at all — its trailing row IS
 * the canvas, so it gets a full-height row rather than the add-row sliver.
 */
export function fullHeightRows(layout: GridLayout): number {
  return layout.contentRows > 0 ? layout.contentRows : 1;
}

/**
 * The slot a section advertises as its default place to add a card: column 1
 * of the first row past the section's own content (the top-left cell when it
 * has no cards). That row is empty by construction, so the slot always
 * exists.
 *
 * Deliberately NOT `empties[0]` — the first empty cell in reading order can
 * be a gap BESIDE the content in a partly-filled row, which reads as an
 * arbitrary place to point at. Deliberately not keyed off
 * {@link GridLayout.contentRows} either: {@link equalizeEmptyRows} inflates
 * that to the tallest section in the visual row, which would push the slot
 * below this section's own content and into the filler rows.
 */
export function defaultAddCell(layout: GridLayout): EmptyCell | undefined {
  const row = maxOccupiedRow(layout.placements) + 1;
  return layout.empties.find((e) => e.rowStart === row && e.colStart === 1);
}
